/**
 * Tier-aware completion model for New Practice Session (scroll / validation helpers).
 */

export type GuidanceSectionId = 'persona' | 'schedule';

export type NewPracticeGuidanceInput = {
  orgFeatures: string[] | null;
  selectedPersonas: string[];
  canPracticeScheduling: boolean;
  sessionTiming: 'now' | 'schedule';
  scheduleStartLocal: string;
  scheduleEndLocal: string;
};

export function getScheduleWindowValidationError(
  canPracticeScheduling: boolean,
  sessionTiming: 'now' | 'schedule',
  scheduleStartLocal: string,
  scheduleEndLocal: string,
): string | null {
  if (!canPracticeScheduling || sessionTiming !== 'schedule') return null;
  const start = new Date(scheduleStartLocal);
  const end = new Date(scheduleEndLocal);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
    return 'Choose a valid start and end time for the schedule.';
  }
  return null;
}

export function isNewPracticeSubmittable(input: NewPracticeGuidanceInput): boolean {
  if (input.orgFeatures === null) return false;
  if (input.selectedPersonas.length === 0) return false;
  return (
    getScheduleWindowValidationError(
      input.canPracticeScheduling,
      input.sessionTiming,
      input.scheduleStartLocal,
      input.scheduleEndLocal,
    ) === null
  );
}

/**
 * How many required checklist items are still incomplete (persona, pitch style, and
 * schedule window when scheduling is on and “schedule” is selected).
 * Returns null while org features are still loading.
 */
export function getNewPracticeMandatoryIncompleteCount(
  input: NewPracticeGuidanceInput,
): number | null {
  if (input.orgFeatures === null) return null;
  let n = 0;
  if (input.selectedPersonas.length === 0) n += 1;
  if (
    input.canPracticeScheduling &&
    input.sessionTiming === 'schedule' &&
    getScheduleWindowValidationError(
      input.canPracticeScheduling,
      input.sessionTiming,
      input.scheduleStartLocal,
      input.scheduleEndLocal,
    ) !== null
  ) {
    n += 1;
  }
  return n;
}

/**
 * First incomplete *required* section for guidance and error scrolling.
 * Returns null when the form is ready to submit (CTA).
 */
export function getFirstBlockingGuidanceSectionId(
  input: NewPracticeGuidanceInput,
): GuidanceSectionId | null {
  if (input.orgFeatures === null) return null;
  if (input.selectedPersonas.length === 0) return 'persona';
  if (
    input.canPracticeScheduling &&
    input.sessionTiming === 'schedule' &&
    getScheduleWindowValidationError(
      input.canPracticeScheduling,
      input.sessionTiming,
      input.scheduleStartLocal,
      input.scheduleEndLocal,
    ) !== null
  ) {
    return 'schedule';
  }
  return null;
}
